/**
 * workspace-readiness.ts
 *
 * Plugin Readiness & Capability Degradation Evaluator (Sprint ONBOARD-01):
 * - Evaluates plugin lifecycle state: available -> selected -> dependency_pending -> configuration_pending -> ready | degraded | blocked.
 * - Evaluates capability-level readiness (ready, not_configured, degraded, blocked).
 * - Distinguishes between test/sandbox vs. production compliance prerequisites.
 * - Implements capability-based degradation rather than monolithic workspace failure.
 */

import { CANONICAL_18_PLUGIN_CATALOG, type PluginManifest } from './plugin-manifests.js';
import type { WorkspaceConfiguration } from './workspace-configuration.js';
import { WorkspaceDependencyResolver } from './workspace-dependencies.js';

export type PluginLifecycleState =
  | 'available'
  | 'selected'
  | 'dependency_pending'
  | 'configuration_pending'
  | 'approval_pending'
  | 'provisioning'
  | 'ready'
  | 'degraded'
  | 'disabled'
  | 'failed';

export interface CapabilityReadinessReport {
  readonly capabilityId: string;
  readonly displayName: string;
  readonly status: 'ready' | 'not_configured' | 'degraded' | 'blocked';
  readonly isRegulated: boolean;
  readonly missingRequirements: readonly string[];
}

export interface PluginReadiness {
  readonly pluginId: string;
  readonly state: PluginLifecycleState;
  readonly status: 'ready' | 'degraded' | 'blocked' | 'disabled';
  readonly missingDependencies: readonly string[];
  readonly missingSetup: readonly string[];
  readonly capabilityReports: readonly CapabilityReadinessReport[];
  readonly availableCapabilities: readonly string[];
  readonly degradedCapabilities: readonly string[];
  readonly degradedMessages: readonly string[];
}

export interface WorkspaceReadinessReport {
  readonly overallStatus:
    | 'not_started'
    | 'configuration_required'
    | 'ready_for_test'
    | 'ready_for_production'
    | 'blocked';
  readonly totalEnabledPlugins: number;
  readonly readyPluginsCount: number;
  readonly degradedPluginsCount: number;
  readonly blockedPluginsCount: number;
  readonly pluginReports: readonly PluginReadiness[];
  readonly blockingIssues: readonly string[];
  readonly warnings: readonly string[];
}

export class WorkspaceReadinessEvaluator {
  private readonly catalogMap = new Map<string, PluginManifest>();
  private readonly resolver: WorkspaceDependencyResolver;

  constructor(customCatalog?: readonly PluginManifest[]) {
    const catalog = customCatalog ?? CANONICAL_18_PLUGIN_CATALOG;
    for (const manifest of catalog) {
      this.catalogMap.set(manifest.pluginId, manifest);
    }
    this.resolver = new WorkspaceDependencyResolver(catalog);
  }

  public evaluatePluginReadiness(
    pluginId: string,
    config: WorkspaceConfiguration
  ): PluginReadiness {
    const isEnabled = config.enabledPlugins.includes(pluginId);
    if (!isEnabled) {
      return {
        pluginId,
        state: 'disabled',
        status: 'disabled',
        missingDependencies: [],
        missingSetup: [],
        capabilityReports: [],
        availableCapabilities: [],
        degradedCapabilities: [],
        degradedMessages: [],
      };
    }

    const manifest = this.catalogMap.get(pluginId);
    if (!manifest) {
      return {
        pluginId,
        state: 'ready',
        status: 'ready',
        missingDependencies: [],
        missingSetup: [],
        capabilityReports: [],
        availableCapabilities: [],
        degradedCapabilities: [],
        degradedMessages: [],
      };
    }

    const enabledSet = new Set(config.enabledPlugins);
    const missingRequired: string[] = [];
    const missingOptional: string[] = [];
    const degradedMessages: string[] = [];

    // 1. Check required dependencies
    for (const reqId of manifest.requiredDependencies) {
      if (!enabledSet.has(reqId)) {
        missingRequired.push(reqId);
        degradedMessages.push(`Missing required dependency '${reqId}'`);
      }
    }

    // 2. Check optional dependencies
    for (const optId of manifest.optionalDependencies) {
      if (!enabledSet.has(optId)) {
        missingOptional.push(optId);
      }
    }

    // 3. Check setup requirements
    const missingSetup: string[] = [];
    for (const step of manifest.setupSteps) {
      if (step.required && !config.pluginSettings[`${pluginId}.${step.stepId}`]) {
        missingSetup.push(step.stepId);
        degradedMessages.push(`Setup required: ${step.label} (${step.description})`);
      }
    }

    // 4. Evaluate Capability-Level Readiness
    const capabilityReports: CapabilityReadinessReport[] = manifest.capabilities.map((cap) => {
      const missingReqs: string[] = [];

      const capSpecificToDependency =
        (cap.capabilityId === 'crm.followups' && missingRequired.includes('ActionPlannerPlugin')) ||
        (cap.capabilityId === 'sprint.plan' && missingRequired.includes('ActionPlannerPlugin')) ||
        (cap.capabilityId === 'pos.checkout' && (missingRequired.includes('CommercePlugin') || missingRequired.includes('HardwarePlugin'))) ||
        (cap.capabilityId.startsWith('delivery.') && missingRequired.includes('WarehousePlugin'));

      const isGeneralBlocked = missingRequired.length > 0 && (capSpecificToDependency || (manifest.capabilities.length === 1 && missingRequired.length > 0));

      if (isGeneralBlocked) {
        missingReqs.push(`Parent dependencies missing: ${missingRequired.join(', ')}`);
      }

      if (cap.requiresConfiguration && missingSetup.length > 0) {
        missingReqs.push('Domain configuration pending');
      }

      let capStatus: CapabilityReadinessReport['status'] = 'ready';
      if (isGeneralBlocked) {
        capStatus = manifest.capabilities.length === 1 ? 'blocked' : 'degraded';
      } else if (cap.requiresConfiguration && missingSetup.length > 0) {
        capStatus = 'not_configured';
      }

      return {
        capabilityId: cap.capabilityId,
        displayName: cap.displayName,
        status: capStatus,
        isRegulated: cap.isRegulated,
        missingRequirements: missingReqs,
      };
    });

    const availableCapabilities = capabilityReports
      .filter((c) => c.status === 'ready')
      .map((c) => c.capabilityId);

    const degradedCapabilities = capabilityReports
      .filter((c) => c.status === 'degraded' || c.status === 'not_configured' || c.status === 'blocked')
      .map((c) => c.capabilityId);

    // Determine Plugin State Machine State
    let state: PluginLifecycleState = 'ready';
    let status: PluginReadiness['status'] = 'ready';

    if (missingRequired.length > 0) {
      state = availableCapabilities.length > 0 ? 'selected' : 'dependency_pending';
      status = availableCapabilities.length > 0 ? 'degraded' : 'blocked';
    } else if (missingSetup.length > 0) {
      state = 'configuration_pending';
      status = availableCapabilities.length > 0 ? 'degraded' : 'blocked';
    } else if (missingOptional.length > 0) {
      state = 'ready';
      status = degradedCapabilities.length > 0 ? 'degraded' : 'ready';
    }

    return {
      pluginId,
      state,
      status,
      missingDependencies: [...missingRequired, ...missingOptional],
      missingSetup,
      capabilityReports,
      availableCapabilities,
      degradedCapabilities,
      degradedMessages,
    };
  }

  public evaluateAll(config: WorkspaceConfiguration): Map<string, PluginReadiness> {
    const result = new Map<string, PluginReadiness>();
    for (const manifest of this.catalogMap.values()) {
      result.set(manifest.pluginId, this.evaluatePluginReadiness(manifest.pluginId, config));
    }
    return result;
  }

  public evaluateWorkspace(config: WorkspaceConfiguration): WorkspaceReadinessReport {
    const readinessMap = this.evaluateAll(config);
    const reports = Array.from(readinessMap.values()).filter((r) => config.enabledPlugins.includes(r.pluginId));

    const blockingIssues: string[] = [];
    const warnings: string[] = [];

    let readyCount = 0;
    let degradedCount = 0;
    let blockedCount = 0;

    for (const r of reports) {
      if (r.status === 'ready') readyCount++;
      else if (r.status === 'degraded') {
        degradedCount++;
        warnings.push(...r.degradedMessages);
      } else if (r.status === 'blocked') {
        blockedCount++;
        blockingIssues.push(...r.degradedMessages);
      }
    }

    let overallStatus: WorkspaceReadinessReport['overallStatus'] = 'ready_for_production';
    if (reports.length === 0) {
      overallStatus = 'not_started';
    } else if (blockedCount > 0) {
      overallStatus = 'blocked';
    } else if (degradedCount > 0) {
      overallStatus = 'configuration_required';
    }

    return {
      overallStatus,
      totalEnabledPlugins: reports.length,
      readyPluginsCount: readyCount,
      degradedPluginsCount: degradedCount,
      blockedPluginsCount: blockedCount,
      pluginReports: reports,
      blockingIssues,
      warnings,
    };
  }
}

export const globalReadinessEvaluator = new WorkspaceReadinessEvaluator();
